import React, { useEffect, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { format, parse, isValid } from 'date-fns';
import 'react-day-picker/dist/style.css';

interface Props {
  value: string;           // YYYY-MM-DD or ''
  onChange: (v: string) => void;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
}

// Parse YYYY-MM-DD without timezone drift
function fromYMD(s: string): Date | undefined {
  if (!s) return undefined;
  const d = parse(s, 'yyyy-MM-dd', new Date());
  return isValid(d) ? d : undefined;
}

const DatePickerInput: React.FC<Props> = ({
  value,
  onChange,
  placeholder = 'Select date',
  minDate,
  maxDate,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = fromYMD(value);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = (day: Date | undefined) => {
    if (day) {
      onChange(format(day, 'yyyy-MM-dd'));
      setOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  const disabled: any[] = [];
  if (minDate) disabled.push({ before: minDate });
  if (maxDate) disabled.push({ after: maxDate });

  return (
    <div ref={rootRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-dark-canvas border border-dark-border rounded-lg text-left min-w-[140px] transition-colors hover:border-primary-500 focus:outline-none focus:border-primary-500"
        style={{ color: selected ? undefined : '#6b7280' }}
      >
        <svg className="w-4 h-4 flex-shrink-0 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span className="flex-1 text-gray-200">
          {selected ? format(selected, 'MMM d, yyyy') : placeholder}
        </span>
        {selected && (
          <span
            onClick={handleClear}
            className="text-gray-500 hover:text-gray-300 text-base leading-none ml-1 cursor-pointer"
            title="Clear"
          >
            &times;
          </span>
        )}
      </button>

      {/* Popover */}
      {open && (
        <div className="absolute z-50 mt-1 bg-dark-card border border-dark-border rounded-xl shadow-2xl p-3 left-0">
          <DayPicker
            mode="single"
            selected={selected}
            onSelect={handleSelect}
            disabled={disabled.length ? disabled : undefined}
            showOutsideDays
            classNames={{
              root:            'text-sm select-none',
              months:          'flex',
              month:           '',
              month_caption:   'flex items-center justify-center relative mb-2 h-7',
              caption_label:   'text-sm font-semibold text-white',
              nav:             'flex items-center gap-1',
              button_previous: 'absolute left-0 p-1 rounded text-gray-400 hover:text-white hover:bg-dark-hover transition-colors',
              button_next:     'absolute right-0 p-1 rounded text-gray-400 hover:text-white hover:bg-dark-hover transition-colors',
              chevron:         'w-4 h-4',
              weekdays:        'flex',
              weekday:         'w-9 text-[10px] font-medium text-gray-500 text-center pb-1',
              weeks:           '',
              week:            'flex mt-0.5',
              day:             'w-9 text-center p-0',
              day_button:      'w-8 h-8 rounded-full text-sm text-gray-300 hover:bg-dark-hover hover:text-white cursor-pointer transition-colors flex items-center justify-center mx-auto',
              selected:        'bg-primary-600 text-white hover:bg-primary-700 rounded-full',
              today:           'font-bold text-primary-400',
              outside:         'text-gray-600 opacity-50',
              disabled:        'text-gray-700 opacity-30 cursor-not-allowed hover:bg-transparent',
              hidden:          'invisible',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default DatePickerInput;
